import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DataTable } from '@/components/DashCharts';
import { fetchClarity, connectClarity, disconnectClarity } from '@/lib/api';
import { formatMetric as fmt } from '@/lib/dashboardReports.mjs';

const pct = value => fmt(value, '%');
const pathOf = url => { try { const u = new URL(url); return u.pathname || '/'; } catch { return url; } };
const seconds = s => s == null ? 'Unavailable' : s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`;

/**
 * Microsoft Clarity for the selected site. Heatmaps and recordings stay in
 * Clarity; this section shows where visitors struggle and links to them.
 * gscPages lets each struggling page show its Google clicks.
 */
export default function ClarityReport({ propertyId, range, isDemo, gscPages = [], onStatus }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [projectId, setProjectId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setError('');
    setReport(null);
    fetchClarity(propertyId, range).then(r => live && setReport(r)).catch(e => live && setError(e.response?.data?.detail || 'Clarity data could not be loaded.'));
    return () => { live = false; };
  }, [propertyId, range]);

  useEffect(() => {
    onStatus?.(error ? 'Unavailable' : !report ? 'Loading' : report.is_demo ? 'Demo data' : !report.connected ? 'Not connected' : report.empty ? 'Connected, first snapshot pending' : `${report.days_collected} of ${report.range_days} days collected`);
  }, [report, error, onStatus]);

  const clicks = new Map((gscPages || []).map(p => [p.page, p.clicks]));
  const googleClicks = url => clicks.get(pathOf(url)) ?? clicks.get(url) ?? null;

  const connect = async e => {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    try {
      const r = await connectClarity(propertyId, token.trim(), projectId.trim(), range);
      setToken(''); setProjectId(''); setReport(r);
      toast.success('Clarity connected. The first snapshot is saved.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Couldn’t connect Clarity');
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { setReport(await disconnectClarity(propertyId)); toast.success('Clarity disconnected and its saved history deleted'); }
    catch { toast.error('Couldn’t disconnect Clarity'); }
    finally { setBusy(false); }
  };

  const columns = [
    { key: 'url', label: 'Page', wide: true, render: (url, row) => <span className="ss-clarity-page">{pathOf(url)}{row.links?.heatmap && <a href={row.links.heatmap} target="_blank" rel="noopener noreferrer">Open heatmaps in Clarity</a>}</span> },
    { key: 'sessions', label: 'Sessions', align: 'right', render: v => fmt(v) },
    { key: 'google_clicks', label: 'Google clicks', align: 'right', render: v => fmt(v) },
    { key: 'quickback_pct', label: 'Quick backs', align: 'right', render: pct },
    { key: 'rage_pct', label: 'Rage clicks', align: 'right', render: pct },
    { key: 'dead_pct', label: 'Dead clicks', align: 'right', render: pct },
    { key: 'scroll_depth', label: 'Scroll depth', align: 'right', render: pct },
  ];
  const top = report?.struggling_pages?.[0];

  return <section id="clarity" className="ss-section" aria-labelledby="clarity-heading" data-testid="clarity-report">
    <h2 id="clarity-heading">How visitors behave on your pages</h2>
    <p>{report?.is_demo ? 'Demo data. ' : ''}From Microsoft Clarity: where visitors rage click, hit dead ends or leave straight away. Clarity covers every traffic channel, not just search. Heatmaps and recordings stay in Clarity, so each page links there.</p>
    {error && <p role="alert" data-testid="clarity-error">{error}</p>}
    {!error && !report && <p>Loading Clarity data…</p>}
    {report && !report.connected && <div className="ss-panel ss-bing-connect" data-testid="clarity-connect">
      <form onSubmit={connect} className="ss-bing-form">
        <label htmlFor="clarity-token">Clarity API token for this site</label>
        <div>
          <input id="clarity-token" type="password" autoComplete="off" value={token} onChange={e => setToken(e.target.value)} placeholder="Paste the Data Export token" data-testid="clarity-token-input" />
          <button type="submit" disabled={busy || !token.trim()} data-testid="clarity-connect-save">{busy ? 'Checking…' : 'Connect Clarity'}</button>
        </div>
        <label htmlFor="clarity-project">Clarity project ID <span className="ss-optional">(optional, for links to heatmaps)</span></label>
        <div><input id="clarity-project" autoComplete="off" value={projectId} onChange={e => setProjectId(e.target.value)} placeholder="e.g. 3t0wlogvdz" data-testid="clarity-project-input" /></div>
      </form>
      <p className="ss-scope">Install Clarity on this site first. Then, in Clarity, open this site’s project, go to Settings, then Data Export, and generate an API token. The project ID is in the project’s web address. SEO Signals checks the token, stores it encrypted and saves one snapshot a day. History builds from today. <a href="https://clarity.microsoft.com/" target="_blank" rel="noopener noreferrer">Open Microsoft Clarity</a></p>
    </div>}
    {report?.connected && <>
      {report.last_error && <p className="ss-clarity-warn" role="status">The last daily snapshot failed: {report.last_error.message}</p>}
      <p className="ss-scope ss-clarity-coverage" data-testid="clarity-coverage">{report.empty ? 'Connected. The first daily snapshot will appear after tonight’s run.' : `${report.days_collected} of ${report.range_days} days collected${report.period ? `, ${report.period.start} to ${report.period.end}` : ''}. Clarity’s API only keeps three days, so SEO Signals saves one snapshot a day.`}</p>
      {!report.empty && <>
        <dl className="ss-detail-metrics ss-bing-totals ss-clarity-totals" data-testid="clarity-totals">
          <div><dt>Sessions</dt><dd>{fmt(report.totals.sessions)}</dd></div>
          <div><dt>Quick backs</dt><dd>{pct(report.totals.quickback_pct)}</dd></div>
          <div><dt>Rage clicks</dt><dd>{pct(report.totals.rage_pct)}</dd></div>
          <div><dt>Dead clicks</dt><dd>{pct(report.totals.dead_pct)}</dd></div>
          <div><dt>Script errors</dt><dd>{pct(report.totals.script_error_pct)}</dd></div>
          <div><dt>Scroll depth</dt><dd>{pct(report.totals.scroll_depth)}</dd></div>
          <div><dt>Active time per session</dt><dd>{seconds(report.totals.active_time)}</dd></div>
        </dl>
        {top && <div className="ss-clarity-callout" data-testid="clarity-callout">
          <strong>Worth watching first: {pathOf(top.url)}</strong>
          <p>{`About ${fmt(top.struggling_sessions)} of ${fmt(top.sessions)} sessions on this page ended in a quick back, rage click or dead click${googleClicks(top.url) != null ? `, and it earned ${fmt(googleClicks(top.url))} Google clicks in this period` : ''}. Watch a few recordings to see what went wrong.`}</p>
          {top.links?.heatmap && <a href={top.links.heatmap} target="_blank" rel="noopener noreferrer">Open heatmaps in Clarity</a>}
        </div>}
        <div className="ss-panel">
          <DataTable testid="clarity-pages-table" columns={columns} rows={report.struggling_pages.map(p => ({ ...p, google_clicks: googleClicks(p.url) }))} emptyMessage="No page had enough sessions with quick backs, rage clicks or dead clicks in this period." />
        </div>
        <p className="ss-scope">Pages with at least 10 sessions, ranked by sessions that ended in a quick back, rage click or dead click. Percentages are the share of sessions where it happened at least once. Web addresses are stored without query strings.</p>
      </>}
      {!report.is_demo && <p className="ss-bing-key">{report.project_id ? <>Clarity project {report.project_id}. {report.dashboard_url && <a href={report.dashboard_url} target="_blank" rel="noopener noreferrer">Open dashboard</a>}</> : 'Clarity connected.'} <button type="button" onClick={remove} disabled={busy} data-testid="clarity-disconnect">Disconnect</button></p>}
    </>}
  </section>;
}
